import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import { PrismBadge, PrismDataBar } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { SymmetryData } from '@mylighthouse/prism-ember/types/components/prism/data-bar';
import { t } from 'ember-intl';
import { and, notEq } from 'ember-truth-helpers';
import HotelAndMarketRevenueInsightBaseSegmentationBlockItemBase, {
  type HotelAndMarketRevenueInsightBaseSegmentationBlockItemBaseSignature,
} from 'lumen/components/hotel-and-market/revenue-insight/base/segmentation-block-item-base';
import HotelAndMarketRevenueInsightTooltipsTooltipRevenueInsight from 'lumen/components/hotel-and-market/revenue-insight/tooltips/tooltip-revenue-insight';

export interface HotelAndMarketRevenueInsightHotelBlockHotelBlockItemSignature extends HotelAndMarketRevenueInsightBaseSegmentationBlockItemBaseSignature {
  Args: HotelAndMarketRevenueInsightBaseSegmentationBlockItemBaseSignature['Args'] & {
    params: HotelAndMarketRevenueInsightBaseSegmentationBlockItemBaseSignature['Args']['params'] & {
      showFullPortfolio: boolean;
      visibleKpis: string[];
    };
  };
}

export default class HotelAndMarketRevenueInsightHotelBlockHotelBlockItem extends HotelAndMarketRevenueInsightBaseSegmentationBlockItemBase<HotelAndMarketRevenueInsightHotelBlockHotelBlockItemSignature> {
  @service declare router: Services['router'];

  @computed(
    'args.item.segmentationValue',
    'riSelections.{fromDate,toDate,refFromDate,reference}',
  )
  get hotelLink(): string {
    const { fromDate, toDate, refFromDate, reference } = this.riSelections;
    const path = this.router.urlFor(
      'hotel.revenue-insight',
      this.args.item.segmentationValue,
      {
        queryParams: {
          fromDate,
          toDate,
          refFromDate,
          reference,
        },
      },
    );

    return path;
  }

  @computed('issues.length', 'showFullPortfolio')
  get isDisabled(): boolean {
    const hasIssues = this.issues && this.issues?.length > 0;
    // Disable the item when it has an issue and full portfolio is not enabled
    const disabledDueToIssues = Boolean(hasIssues) && !this.showFullPortfolio;
    return super.isDisabled || disabledDueToIssues;
  }

  // Added a check on isMultiProperty and on isOccupancySelected to hide or show the suffixes
  get isOccupancySelected(): boolean {
    return (
      this.riSelections?.isOccupancySelected &&
      this.riSelections?.isMultiProperty
    );
  }

  @computed('intl', 'issues.length')
  get issueLabels(): string[] {
    const issueLabels = [];
    if (this.issues) {
      // Add a label for the first issue in the array (sorted by priority)
      issueLabels.push(this.intl.t(`revenue_insight.issue.${this.issues[0]}`));
      const numOfOtherIssues = this.issues.length - 1;
      if (numOfOtherIssues > 0) {
        // Add a label with the number of other issues
        issueLabels.push(`+${numOfOtherIssues}`);
      }
    }
    return issueLabels;
  }

  @computed('args.item.meta.issues')
  get issues(): string[] | undefined {
    return this.args.item?.meta?.issues;
  }

  @computed('args.params.showFullPortfolio')
  get showFullPortfolio(): boolean {
    return this.args.params?.showFullPortfolio;
  }

  @dependentKeyCompat
  get showPercentages(): boolean {
    return this.isOccupancySelected;
  }

  get symmetryData(): SymmetryData {
    return {
      value: this.changeValue,
      formattedValue: this.formattedChangeValueWithPercentage,
      positiveWidthPercentage: this.positiveReferenceValuePercentage,
      negativeWidthPercentage: this.negativeReferenceValuePercentage,
    } as SymmetryData;
  }

  <template>
    {{! template-lint-disable no-nested-interactive require-presentational-children }}
    <div
      class="button--unstyled block-list-item-container block-list-item-container--hotel
        {{if this.isSelected 'segmentation-item--selected'}}
        {{if @item.isSelectable 'segmentation-item--selectable'}}"
      {{! @glint-expect-error: Incorrect type }}
      disabled={{this.isDisabled}}
      role="button"
      {{on "mouseenter" this.onMouseEnterTask.perform}}
      {{on "mouseleave" this.onMouseLeaveTask.perform}}
    >
      <button
        class="button--unstyled subscription-title-column flex items-center justify-start"
        disabled={{this.isDisabled}}
        type="button"
        {{on "click" (fn this.selectSegmentationItem @item)}}
      >
        <p
          class="subscription-title text-200--regular truncate
            {{if this.isDisabled 'segmentation-item--disabled'}}"
        >
          {{@item.displayValue}}
        </p>
        {{#each this.issueLabels as |issue|}}
          <LinkTo
            @route="market.revenue-insight.issue-table"
            data-event-id="market-revenue-insight-open-hotel-issue-modal"
            data-event-value="issue-label"
          >
            <PrismBadge @label={{issue}} @type="warning" />
          </LinkTo>
        {{/each}}
      </button>
      <div
        class="sortable-column flex justify-between"
        role="button"
        {{on "click" (fn this.selectSegmentationItem @item)}}
      >
        {{#if this.hasTarget}}
          <div class="current-value current-value--target">
            <PrismDataBar
              @data={{this.valueProgressBarData}}
              @targetValue={{if
                (notEq this.targetValue null)
                this.targetValuePercentage
              }}
              as |DataBar|
            >
              <DataBar.value @text={{this.formattedValueWithPercentage}} />
            </PrismDataBar>
          </div>
          <div class="reference-value reference-value--target">
            {{#if this.riSelections.isBudgetActive}}
              <div
                class="reference-value__title"
              >{{this.riSelections.vsBudgetText}}</div>
            {{else}}
              <div
                class="reference-value__title"
              >{{this.riSelections.vsForecastText}}</div>
            {{/if}}
            <div
              style={{this.valueColor}}
            >{{this.formattedChangeValueWithPercentage}}</div>
          </div>
        {{else}}
          <div class="current-value">
            <PrismDataBar @data={{this.valueProgressBarData}} as |DataBar|>
              <DataBar.value @text={{this.formattedValueWithPercentage}} />
            </PrismDataBar>
          </div>
          <div class="reference-value">
            <PrismDataBar @data={{this.symmetryData}} @type="symmetric" />
          </div>
        {{/if}}
      </div>
      <a
        class="prism-link redirect-to-hotel--minimal truncate"
        data-event-id="market-revenue-insight-redirect-to-hotel"
        href={{this.hotelLink}}
        rel="noopener noreferrer"
        target="_blank"
      >
        {{t "general.hotel_view"}}
      </a>
    </div>
    {{#if (and this.isHovered this.showTooltip)}}
      <EmberTooltip
        @isShown={{this.isHovered}}
        @tooltipClass="base-tooltip revenue-insight-segmentation-value-tooltip"
      >
        <HotelAndMarketRevenueInsightTooltipsTooltipRevenueInsight
          @data={{@item}}
          @kpis={{@params.visibleKpis}}
          @metricKey={{this.metricKey}}
        />
      </EmberTooltip>
    {{/if}}
  </template>
}
